
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserData } from "@/hooks/use-auth-session";
import { useAuthOperations } from "@/hooks/use-auth-operations";

interface AuthContextType {
  user: { email: string; fullName: string } | null;
  isLoading: boolean;
  sendLoginCode: (email: string, fullName: string) => Promise<boolean>;
  verifyLoginCode: (email: string, code: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<{ email: string; fullName: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { sendLoginCode, verifyLoginCode, logout: logoutOperation, isLoading: operationLoading } = useAuthOperations();

  // Initialize auth state from localStorage
  useEffect(() => {
    const loadUserFromStorage = () => {
      const storedUser = getUserData();
      if (storedUser) {
        setUser(storedUser);
      }
      setIsLoading(false);
    };

    loadUserFromStorage();
  }, []);

  // Extended logout function with navigation
  const logout = async (): Promise<void> => {
    await logoutOperation();
    setUser(null);
    navigate("/");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading: isLoading || operationLoading,
        sendLoginCode,
        verifyLoginCode: async (email, code) => {
          console.log("Verifying login code in AuthContext:", email, code);
          const success = await verifyLoginCode(email, code);
          if (success) {
            // Refresh user data after successful verification
            const userData = getUserData();
            setUser(userData);
          }
          return success;
        },
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
